import React, { useMemo, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import './delete-verification-modal.css';

const formatCurrency = (amount) => new Intl.NumberFormat('sv-SE', {
  style: 'currency',
  currency: 'SEK',
}).format(Number(amount) || 0);

const DeleteVerificationModal = ({
  show,
  handleClose,
  verificationData,
  companyId,
  onVerificationDeleted,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const reversalTransactions = useMemo(() => (
    (verificationData?.transactions || []).map((transaction) => ({
      ...transaction,
      transaction_type: transaction.transaction_type === 'debit' ? 'credit' : 'debit',
    }))
  ), [verificationData?.transactions]);

  const total = useMemo(() => (
    (verificationData?.transactions || []).reduce((sum, transaction) => (
      transaction.transaction_type === 'debit'
        ? sum + (Number(transaction.amount) || 0)
        : sum
    ), 0)
  ), [verificationData?.transactions]);

  const handleDelete = async () => {
    if (!verificationData?.verification_id || isProcessing) return;

    setIsProcessing(true);
    try {
      await axios.delete(getApiUrl(`${companyId}/verifications/${verificationData.verification_id}`), {
        data: { allowFullDeletion: false },
        withCredentials: true,
      });

      toast.success('Verifikatet har raderats genom ett annulleringsverifikat.');
      onVerificationDeleted?.();
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Det gick inte att radera verifikatet.');
    } finally {
      setIsProcessing(false);
    }
  };

  const verificationLabel = `${verificationData?.series || ''}${verificationData?.number || ''}` || '—';

  return (
    <Modal
      show={show}
      onHide={isProcessing ? undefined : handleClose}
      centered
      className="delete-verification-modal"
    >
      <Modal.Header closeButton={!isProcessing}>
        <div>
          <span className="delete-verification-modal__eyebrow">Verifikat {verificationLabel}</span>
          <Modal.Title>Radera verifikat</Modal.Title>
        </div>
      </Modal.Header>

      <Modal.Body>
        <p className="delete-verification-modal__explanation">
          Bokföringshistoriken får inte tas bort. LedgerLoop skapar därför automatiskt ett nytt
          verifikat som tar ut effekten av {verificationLabel}.
        </p>

        <div className="delete-verification-modal__summary">
          <div>
            <strong>{verificationData?.description || 'Utan beskrivning'}</strong>
            <span>
              {verificationData?.date ? moment(verificationData.date).format('D MMMM YYYY') : 'Datum saknas'}
            </span>
          </div>
          <strong>{formatCurrency(total)}</strong>
        </div>

        <details className="delete-verification-modal__details">
          <summary>
            Visa konteringen som skapas
            <ChevronDown size={15} aria-hidden="true" />
          </summary>
          <div className="delete-verification-modal__table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Konto</th>
                  <th>Kontonamn</th>
                  <th className="is-amount">Debet</th>
                  <th className="is-amount">Kredit</th>
                </tr>
              </thead>
              <tbody>
                {reversalTransactions.map((transaction, index) => (
                  <tr key={transaction.transaction_id || `${transaction.account_number}-${index}`}>
                    <td className="is-account">{transaction.account_number || '—'}</td>
                    <td>{transaction.account_name || '—'}</td>
                    <td className="is-amount">
                      {transaction.transaction_type === 'debit' ? formatCurrency(transaction.amount) : '—'}
                    </td>
                    <td className="is-amount">
                      {transaction.transaction_type === 'credit' ? formatCurrency(transaction.amount) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>

        <p className="delete-verification-modal__final-note">
          Åtgärden kan inte ångras.
        </p>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="delete-verification-modal__cancel"
          onClick={handleClose}
          disabled={isProcessing}
        >
          Avbryt
        </button>
        <button
          type="button"
          className="delete-verification-modal__confirm"
          onClick={handleDelete}
          disabled={isProcessing}
        >
          {isProcessing && <Spinner animation="border" size="sm" />}
          {isProcessing ? 'Raderar…' : 'Radera verifikat'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteVerificationModal;
